import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, Upload, Image as ImageIcon, 
  GripVertical, Star, Eye, ExternalLink, Video,
  Sparkles, Shield, Palette, Clock, Heart, Gift, Award
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  getLandingConfig, 
  updateLandingConfig, 
  uploadLandingGalleryImage,
  deleteLandingGalleryImage,
  uploadLandingVideo,
  deleteLandingVideo
} from '../../services/api';

const ICON_OPTIONS = [
  { value: 'Sparkles', label: 'Brilho', icon: Sparkles },
  { value: 'Shield', label: 'Escudo', icon: Shield },
  { value: 'Palette', label: 'Paleta', icon: Palette },
  { value: 'Clock', label: 'Relógio', icon: Clock },
  { value: 'Heart', label: 'Coração', icon: Heart },
  { value: 'Gift', label: 'Presente', icon: Gift },
  { value: 'Award', label: 'Prêmio', icon: Award },
];

const LandingSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const data = await getLandingConfig();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching landing config:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLandingConfig(config);
      toast.success('Landing page atualizada com sucesso!');
    } catch (error) {
      console.error('Error saving landing config:', error);
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const result = await uploadLandingGalleryImage(file);
      setConfig(prev => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), result.image]
      }));
      toast.success('Imagem adicionada!');
    } catch (error) {
      toast.error('Erro ao fazer upload');
    }
    e.target.value = '';
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Remover esta imagem?')) return;
    try {
      await deleteLandingGalleryImage(imageId);
      setConfig(prev => ({
        ...prev,
        gallery_images: prev.gallery_images.filter(img => img.id !== imageId)
      }));
      toast.success('Imagem removida');
    } catch (error) {
      toast.error('Erro ao remover imagem');
    }
  };

  // Feature management
  const addFeature = () => {
    const newFeature = {
      id: Date.now().toString(),
      icon: 'Sparkles',
      title: 'Nova Feature',
      description: 'Descrição da feature',
      active: true
    };
    setConfig(prev => ({
      ...prev,
      features: [...(prev.features || []), newFeature]
    }));
  };

  const updateFeature = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      features: prev.features.map(f => f.id === id ? { ...f, [field]: value } : f)
    }));
  };

  const deleteFeature = (id) => {
    setConfig(prev => ({
      ...prev,
      features: prev.features.filter(f => f.id !== id)
    }));
  };

  // Testimonial management
  const addTestimonial = () => {
    const newTestimonial = {
      id: Date.now().toString(),
      name: 'Nome do Cliente',
      text: 'Depoimento do cliente...',
      rating: 5,
      active: true
    };
    setConfig(prev => ({
      ...prev,
      testimonials: [...(prev.testimonials || []), newTestimonial]
    }));
  };

  const updateTestimonial = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      testimonials: prev.testimonials.map(t => t.id === id ? { ...t, [field]: value } : t)
    }));
  };

  const deleteTestimonial = (id) => {
    setConfig(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter(t => t.id !== id)
    }));
  };

  // Quality points management
  const addQualityPoint = () => {
    setConfig(prev => ({
      ...prev,
      quality_points: [...(prev.quality_points || []), 'Novo ponto de qualidade']
    }));
  };

  const updateQualityPoint = (index, value) => {
    setConfig(prev => ({
      ...prev,
      quality_points: prev.quality_points.map((p, i) => i === index ? value : p)
    }));
  };

  const deleteQualityPoint = (index) => {
    setConfig(prev => ({
      ...prev,
      quality_points: prev.quality_points.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#195C75] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'hero', label: 'Hero' },
    { id: 'stats', label: 'Estatísticas' },
    { id: 'features', label: 'Features' },
    { id: 'quality', label: 'Qualidade' },
    { id: 'testimonials', label: 'Depoimentos' },
    { id: 'gallery', label: 'Galeria' },
    { id: 'cta', label: 'CTA Final' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Landing Page - Cevadores</h2>
          <p className="text-sm text-slate-500">Edite o conteúdo da página de marketing</p>
        </div>
        <div className="flex gap-2">
          <a
            href="/cevadores"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
          >
            <Eye className="w-4 h-4" />
            Visualizar
            <ExternalLink className="w-3 h-3" />
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#195C75] hover:bg-[#195C75]/90"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white text-[#195C75] shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {/* Hero Section */}
        {activeTab === 'hero' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-slate-800 border-b pb-2">Seção Hero</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Badge</label>
                <Input
                  value={config.hero_badge || ''}
                  onChange={(e) => updateField('hero_badge', e.target.value)}
                  placeholder="Ex: Qualidade Premium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título Principal</label>
                <Input
                  value={config.hero_title || ''}
                  onChange={(e) => updateField('hero_title', e.target.value)}
                  placeholder="Cevadores"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtítulo</label>
                <Input
                  value={config.hero_subtitle || ''}
                  onChange={(e) => updateField('hero_subtitle', e.target.value)}
                  placeholder="Personalizados"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Botão Principal</label>
                <Input
                  value={config.hero_cta_primary || ''}
                  onChange={(e) => updateField('hero_cta_primary', e.target.value)}
                  placeholder="Monte seu Pedido"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
              <Textarea
                value={config.hero_description || ''}
                onChange={(e) => updateField('hero_description', e.target.value)}
                rows={3}
                placeholder="Descrição do hero..."
              />
            </div>
          </div>
        )}

        {/* Stats Section */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-slate-800 border-b pb-2">Estatísticas</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <label className="block text-sm font-medium text-slate-700 mb-2">Clientes</label>
                <Input
                  value={config.stat_clients || ''}
                  onChange={(e) => updateField('stat_clients', e.target.value)}
                  placeholder="500+"
                  className="mb-2"
                />
                <Input
                  value={config.stat_clients_label || ''}
                  onChange={(e) => updateField('stat_clients_label', e.target.value)}
                  placeholder="Clientes Satisfeitos"
                  className="text-sm"
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <label className="block text-sm font-medium text-slate-700 mb-2">Produtos</label>
                <Input
                  value={config.stat_products || ''}
                  onChange={(e) => updateField('stat_products', e.target.value)}
                  placeholder="10k+"
                  className="mb-2"
                />
                <Input
                  value={config.stat_products_label || ''}
                  onChange={(e) => updateField('stat_products_label', e.target.value)}
                  placeholder="Cevadores Produzidos"
                  className="text-sm"
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <label className="block text-sm font-medium text-slate-700 mb-2">Avaliação</label>
                <Input
                  value={config.stat_rating || ''}
                  onChange={(e) => updateField('stat_rating', e.target.value)}
                  placeholder="5"
                  className="mb-2"
                />
                <Input
                  value={config.stat_rating_label || ''}
                  onChange={(e) => updateField('stat_rating_label', e.target.value)}
                  placeholder="Avaliação"
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        {activeTab === 'features' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-800">Features / Diferenciais</h3>
              <Button onClick={addFeature} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título da Seção</label>
                <Input
                  value={config.features_title || ''}
                  onChange={(e) => updateField('features_title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtítulo</label>
                <Input
                  value={config.features_subtitle || ''}
                  onChange={(e) => updateField('features_subtitle', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              {(config.features || []).map((feature, index) => {
                const IconComponent = ICON_OPTIONS.find(i => i.value === feature.icon)?.icon || Sparkles;
                return (
                  <div key={feature.id} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#195C75]/10 rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-[#195C75]" />
                      </div>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <select
                          value={feature.icon}
                          onChange={(e) => updateFeature(feature.id, 'icon', e.target.value)}
                          className="w-32 px-2 py-1 border border-slate-200 rounded-lg text-sm"
                        >
                          {ICON_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <Input
                          value={feature.title}
                          onChange={(e) => updateFeature(feature.id, 'title', e.target.value)}
                          placeholder="Título"
                          className="flex-1"
                        />
                      </div>
                      <Input
                        value={feature.description}
                        onChange={(e) => updateFeature(feature.id, 'description', e.target.value)}
                        placeholder="Descrição"
                      />
                    </div>
                    <button
                      onClick={() => deleteFeature(feature.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quality Section */}
        {activeTab === 'quality' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-slate-800 border-b pb-2">Seção de Qualidade</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                <Input
                  value={config.quality_title || ''}
                  onChange={(e) => updateField('quality_title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Destaque</label>
                <Input
                  value={config.quality_highlight || ''}
                  onChange={(e) => updateField('quality_highlight', e.target.value)}
                  placeholder="Premium"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
              <Textarea
                value={config.quality_description || ''}
                onChange={(e) => updateField('quality_description', e.target.value)}
                rows={3}
              />
            </div>

            {/* Video Section */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Video className="w-5 h-5 text-[#195C75]" />
                <label className="block text-sm font-medium text-slate-700">Vídeo da Seção</label>
              </div>
              
              {/* Show current video if uploaded */}
              {config.video_file_url && (
                <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                  <video 
                    src={config.video_file_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${config.video_file_url}` : config.video_file_url}
                    controls
                    className="w-full h-full object-contain"
                  />
                  <button
                    onClick={async () => {
                      if (window.confirm('Remover este vídeo?')) {
                        try {
                          await deleteLandingVideo();
                          setConfig(prev => ({ ...prev, video_file_url: null }));
                          toast.success('Vídeo removido');
                        } catch (error) {
                          toast.error('Erro ao remover vídeo');
                        }
                      }
                    }}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Upload video option */}
              {!config.video_file_url && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Carregar Vídeo (MP4, WebM)</label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/ogg,video/quicktime"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          // Check file size (max 100MB)
                          if (file.size > 100 * 1024 * 1024) {
                            toast.error('Arquivo muito grande. Máximo 100MB');
                            return;
                          }
                          
                          setUploadingVideo(true);
                          setVideoProgress(0);
                          
                          try {
                            const result = await uploadLandingVideo(file, (progress) => {
                              setVideoProgress(progress);
                            });
                            setConfig(prev => ({ ...prev, video_file_url: result.video_url, video_url: null }));
                            toast.success('Vídeo carregado com sucesso!');
                          } catch (error) {
                            console.error('Video upload error:', error);
                            toast.error('Erro ao carregar vídeo');
                          } finally {
                            setUploadingVideo(false);
                            setVideoProgress(0);
                          }
                          e.target.value = '';
                        }}
                        className="flex-1 text-sm file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#195C75] file:text-white file:cursor-pointer hover:file:bg-[#195C75]/90"
                        disabled={uploadingVideo}
                      />
                    </div>
                    {uploadingVideo && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-[#195C75] h-2 rounded-full transition-all duration-300"
                              style={{ width: `${videoProgress}%` }}
                            />
                          </div>
                          <span>{videoProgress}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="relative flex items-center my-2">
                    <div className="flex-1 border-t border-slate-300"></div>
                    <span className="px-3 text-xs text-slate-400 bg-slate-50">OU</span>
                    <div className="flex-1 border-t border-slate-300"></div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">URL do Vídeo (YouTube/Vimeo)</label>
                    <Input
                      value={config.video_url || ''}
                      onChange={(e) => updateField('video_url', e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-700">Pontos de Qualidade</label>
                <Button onClick={addQualityPoint} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {(config.quality_points || []).map((point, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={point}
                      onChange={(e) => updateQualityPoint(index, e.target.value)}
                      className="flex-1"
                    />
                    <button
                      onClick={() => deleteQualityPoint(index)}
                      className="text-red-500 hover:text-red-700 px-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Testimonials Section */}
        {activeTab === 'testimonials' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-800">Depoimentos</h3>
              <Button onClick={addTestimonial} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Título da Seção</label>
              <Input
                value={config.testimonials_title || ''}
                onChange={(e) => updateField('testimonials_title', e.target.value)}
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {(config.testimonials || []).map((testimonial) => (
                <div key={testimonial.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          onClick={() => updateTestimonial(testimonial.id, 'rating', star)}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`w-4 h-4 ${
                              star <= testimonial.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-slate-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => deleteTestimonial(testimonial.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    value={testimonial.name}
                    onChange={(e) => updateTestimonial(testimonial.id, 'name', e.target.value)}
                    placeholder="Nome do cliente"
                  />
                  <Textarea
                    value={testimonial.text}
                    onChange={(e) => updateTestimonial(testimonial.id, 'text', e.target.value)}
                    placeholder="Depoimento..."
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Section */}
        {activeTab === 'gallery' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-800">Galeria de Imagens</h3>
              <label className="flex items-center gap-2 px-4 py-2 bg-[#195C75] text-white rounded-lg cursor-pointer hover:bg-[#195C75]/90">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Upload className="w-4 h-4" />
                Adicionar Imagem
              </label>
            </div>
            
            <p className="text-sm text-slate-500">
              Essas imagens aparecem no carousel do hero da landing page. Recomendado: imagens quadradas (1:1).
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(config.gallery_images || []).map((image) => (
                <div key={image.id} className="relative group aspect-square bg-slate-100 rounded-lg overflow-hidden">
                  <img
                    src={image.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url}
                    alt={image.alt || 'Gallery'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
              
              {(!config.gallery_images || config.gallery_images.length === 0) && (
                <div className="col-span-full py-12 text-center text-slate-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma imagem na galeria</p>
                  <p className="text-sm">Adicione imagens dos seus cevadores</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA Section */}
        {activeTab === 'cta' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-slate-800 border-b pb-2">Chamada Final (CTA)</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
              <Input
                value={config.cta_title || ''}
                onChange={(e) => updateField('cta_title', e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
              <Textarea
                value={config.cta_description || ''}
                onChange={(e) => updateField('cta_description', e.target.value)}
                rows={2}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingSection;
